<template>
  <div v-show="filters.length" class="applied-filters-bar mb-2">
    <div class="heading-sm">Applied Filters</div>
    <div class="d-flex flex-wrap gap-2">
      <div
        v-for="filter in filters"
        :key="filter.key"
        class="chip"
      >
        <span class="chip-label">{{ filter.displayName }}</span>
        <span class="chip-value">{{ filter.displayValue }}</span>
        <GuButton
          variant="secondary"
          size="sm"
          outline
          class="chip-remove"
          icon="bx-x"
          @click="remove(filter)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, inject } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { watchDebounced } from '@shared/libs/vueUse'

import { injectRowModelType } from '@gu-datagrid-new/composables/RowModelType'

const props = defineProps({
  gridApi: {
    type: Object,
    required: true,
  },
})

const emits = defineEmits(['clearPreset'])

const gridState = inject('gridState')
const presetState = inject('presetState', null)
// eslint-disable-next-line no-unused-vars
const rowModelType = injectRowModelType()
const isPresetAction = inject('presetActionState', ref(false))

const filters = ref([])

const formatFilterModel = (filterModel) => {
  return Object.entries(filterModel).map(([colId, filterData]) => {
      const column = props.gridApi.getColumns()?.find((col) => col.getColId() === colId)
      const displayName = column?.getColDef()?.headerName || colId

      let displayValue = ''
      if (filterData.filterType === 'text') {
        displayValue = filterData.filter
      } else if (filterData.filterType === 'number') {
        displayValue = filterData.filter
        if (filterData.type === 'inRange') {
          displayValue = `${filterData.filter} - ${filterData.filterTo}`
        }
      } else if (filterData.filterType === 'date') {
        displayValue = filterData.dateFrom
      } else if (filterData.filterType === 'set') {
        displayValue = filterData.values.join(', ')
      } else {
        displayValue = JSON.stringify(filterData)
      }

      return {
        key: colId,
        displayName,
        displayValue,
        isQuickFilter: false,
        isPreset: false,
      }
    })
}

watchDebounced(
  [gridState.filterModel, () => presetState?.selectedPresetId],
  ([newFilterModel, newPresetId], [oldFilterModel, oldPresetId]) => {
    if (!props.gridApi) {
      filters.value = []
      return
    }

    const filterModelChanged = JSON.stringify(newFilterModel) !== JSON.stringify(oldFilterModel)
    const presetChanged = newPresetId !== oldPresetId

    if (!filterModelChanged && !presetChanged) {
      console.info('[Datagrid] No change in filter model or preset, skipping update')
      return
    }

    const modelFilters = formatFilterModel(newFilterModel)

    const presetFilter = presetState?.selectedPresetId && presetState?.selectedPreset?.name
      ? [{
          key: `preset-${presetState.selectedPresetId}`,
          displayName: 'Preset',
          displayValue: presetState.selectedPreset.name,
          isQuickFilter: false,
          isPreset: true,
        }]
      : []

    filters.value = [...modelFilters, ...presetFilter]
  },
  { debounce: 100, maxWait: 500 },
)

const remove = (filter) => {
  if (filter.isPreset) clearPreset()
  else if (!filter.isQuickFilter) removeFilter(filter.key)
  else clearQuickFilter()
}

const removeFilter = (colId) => {
  if (!props.gridApi) return
  const currentModel = props.gridApi.getFilterModel()
  delete currentModel[colId]
  gridState.setFilterModel(props.gridApi, currentModel)
}

const clearQuickFilter = () => {
  console.info('clearQuickFilter called - not supported for serverSide row model')
}

const clearPreset = () => {
  if (!props.gridApi) return
  emits('clearPreset')
}

const onFilterChanged = () => {
  if (!props.gridApi) {
    console.warn('gridApi not available')
    return
  }

  if (isPresetAction.value) {
    console.info('Skipping setFilterModel due to preset action')
    return
  }

  const newModel = props.gridApi.getFilterModel()
  const currentModelJSON = JSON.stringify(gridState.filterModel.value)
  const newModelJSON = JSON.stringify(newModel)
  if (currentModelJSON !== newModelJSON) {
    gridState.filterModel.value = newModel
  }
}

onMounted(() => {
  if (props.gridApi) {
    const gridFilterModel = props.gridApi.getFilterModel()

    gridState.filterModel.value = props.gridApi.getFilterModel()
    props.gridApi.addEventListener('filterChanged', onFilterChanged)

    if (Object.keys(gridFilterModel).length > 0) {
      filters.value = [...formatFilterModel(gridFilterModel)]
    }
  } else {
    console.warn('[Datagrid] gridApi not available on mount')
  }
})

onUnmounted(() => {
  if (props.gridApi) {
    props.gridApi.removeEventListener('filterChanged', onFilterChanged)
  }
})
</script>

<style lang="scss">
.applied-filters-bar {
  display: flex;
  flex-direction: column;
  row-gap: .25rem;
  padding: .5rem;
  border-radius: var(--gu-border-radius);
  background-color: var(--gu-primary-bg-subtle);

  @media screen and (min-width: 992px) {
    flex-direction: row;
    align-items: center;
    column-gap: .75rem;
    padding-inline-start: 1rem;
  }
}

// TODO: move this to the theme, as global styles
// or - better - extract into a GuChip component
.chip {
  display: flex;
  align-items: center;
  gap: .25rem;
  padding: .125rem;
  background-color: var(--gu-white);
  border-radius: var(--gu-border-radius-sm);
  font-size: .875rem;
}

.chip-label {
  padding-inline-start: .5rem;
  color: var(--gu-emphasis-color);
  font-weight: bold;

  &::after {
    content: ":";
  }
}
.chip-remove.btn-icon {
  --gu-btn-border-width: 0;
  --gu-btn-icon-size: 1.5rem;
}
</style>
